import { useEffect, useState, useRef } from 'react';
import { isScrollable } from './scrollUtils';
import { NON_SCROLLABLE_REVEAL_DELAY } from './constants';

/**
 * Hook that manages footer reveal on non-scrollable pages
 *
 * On pages that aren't scrollable, the footer will automatically reveal
 * after a delay once the user has interacted with the page.
 *
 * @param scrollParent - The scroll container to check for scrollability
 * @param hasInteracted - Whether the user has interacted with the page
 * @returns Progress value (0-1) for revealing the footer
 */
export function useNonScrollableReveal(
  scrollParent: HTMLElement | Window | null,
  hasInteracted: boolean
): number {
  const [revealProgress, setRevealProgress] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Reset reveal progress if page becomes scrollable
    if (!scrollParent || isScrollable(scrollParent)) {
      setRevealProgress(0);
      return;
    }

    // Don't reveal until user has interacted
    if (!hasInteracted) {
      return;
    }

    // Page is non-scrollable and user has interacted - reveal after delay
    timeoutRef.current = setTimeout(() => {
      setRevealProgress(1);
    }, NON_SCROLLABLE_REVEAL_DELAY);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [scrollParent, hasInteracted]);

  return revealProgress;
}
